"use client";

import React from "react";
import { BlurFade } from "@/components/ui/blur-fade";
import { Separator } from "@/components/ui/separator";
import { CheckCircle } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface AlternatingFeature {
  title: string;
  description: string;
  bulletPoints?: string[];
  image?: string;
}

interface AlternatingFeaturesProps {
  features: AlternatingFeature[];
}

const AlternatingFeatures = ({ features }: AlternatingFeaturesProps) => {
  if (!features || features.length === 0) return null;

  return (
    <section className="py-24 px-6 bg-white">
      <div className="container max-w-5xl mx-auto space-y-16">
        {features.map((feature, i) => {
          const isEven = i % 2 === 0;
          return (
            <React.Fragment key={i}>
              {i > 0 && (
                <Separator className="bg-foreground/5" />
              )}
              <div
                className={`grid md:grid-cols-2 gap-12 items-center ${
                  !isEven ? "md:direction-rtl" : ""
                }`}
              >
                {/* Text Column */}
                <div className={`space-y-5 ${!isEven ? "md:order-2" : "md:order-1"}`}>
                  <BlurFade delay={0.1 + i * 0.1}>
                    <h3 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
                      {feature.title}
                    </h3>
                  </BlurFade>
                  <BlurFade delay={0.15 + i * 0.1}>
                    <div className="text-foreground/60 leading-relaxed text-lg prose prose-p:text-foreground/60 prose-p:leading-relaxed prose-p:text-lg prose-p:mb-3 prose-strong:text-foreground prose-a:text-primary prose-a:underline prose-ul:my-3 prose-li:text-foreground/60 max-w-none">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {feature.description}
                      </ReactMarkdown>
                    </div>
                  </BlurFade>
                  {feature.bulletPoints && feature.bulletPoints.length > 0 && (
                    <BlurFade delay={0.2 + i * 0.1}>
                      <ul className="space-y-2.5 pt-2">
                        {feature.bulletPoints.map((point, j) => (
                          <li key={j} className="flex items-start gap-3 text-foreground/70">
                            <CheckCircle size={18} className="text-primary mt-0.5 flex-shrink-0" />
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </BlurFade>
                  )}
                </div>

                {/* Image/Placeholder Column */}
                <BlurFade delay={0.2 + i * 0.1}>
                  <div className={`${!isEven ? "md:order-1" : "md:order-2"}`}>
                    {feature.image ? (
                      <div className="rounded-3xl overflow-hidden shadow-lg">
                        <img
                          src={feature.image}
                          alt={feature.title}
                          className="w-full h-auto object-cover"
                        />
                      </div>
                    ) : (
                      <div className="aspect-[4/3] rounded-3xl bg-muted flex items-center justify-center">
                        <div className="space-y-3 text-center p-6">
                          <div className="w-14 h-14 rounded-2xl bg-foreground/5 flex items-center justify-center mx-auto">
                            <div className="w-7 h-7 rounded-lg bg-foreground/10" />
                          </div>
                          <div className="space-y-1.5">
                            <div className="h-2.5 w-28 bg-foreground/10 rounded-full mx-auto" />
                            <div className="h-2.5 w-20 bg-foreground/10 rounded-full mx-auto" />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </BlurFade>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </section>
  );
};

export default AlternatingFeatures;
